import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { CalendarReservation, ReservationDetailModal, statusChipColor } from './ReservationDetailModal'
import { addDays, isoDate, normalizeDate, normalizeTime, startOfWeek } from './dateUtils'

const MAX_CHIPS_PER_DAY = 3

interface MonthCalendarProps {
  onJumpToWeek: (isoDate: string) => void
}

export const MonthCalendar: React.FC<MonthCalendarProps> = ({ onJumpToWeek }) => {
  const { t, i18n } = useTranslation()
  const [monthStart, setMonthStart] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [selected, setSelected] = useState<CalendarReservation | null>(null)

  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB'
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  const today = isoDate(new Date())

  const lastDay = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0)
  const gridStart = startOfWeek(monthStart)
  const gridEnd = addDays(startOfWeek(lastDay), 6)
  const dateFrom = isoDate(gridStart)
  const dateTo = isoDate(gridEnd)

  const { data, isLoading } = useQuery({
    queryKey: ['reservations', 'month', dateFrom],
    queryFn: async () => {
      const params = new URLSearchParams({ dateFrom, dateTo, limit: '500' })
      const response = await api.get(`/reservations?${params.toString()}`)
      return response.data.data as CalendarReservation[]
    },
  })

  const reservations = data || []
  const byDate = new Map<string, CalendarReservation[]>()
  for (const r of reservations) {
    const key = normalizeDate(r.reservation_date)
    const list = byDate.get(key) || []
    list.push(r)
    byDate.set(key, list)
  }
  for (const list of byDate.values()) {
    list.sort((a, b) => normalizeTime(a.reservation_time).localeCompare(normalizeTime(b.reservation_time)))
  }

  const weeks: Date[][] = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(d, i)))
  }

  const dayNames = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(addDays(startOfWeek(new Date()), i))
  )

  const monthTitle = cap(new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(monthStart))

  const shiftMonth = (delta: number) =>
    setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1))

  return (
    <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h2 className="font-medium">{monthTitle}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => shiftMonth(-1)}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            title={t('reservations.prevMonth')}
          >
            <Icon name="chevron-left" className="w-4" />
          </button>
          <button
            onClick={() => {
              const now = new Date()
              setMonthStart(new Date(now.getFullYear(), now.getMonth(), 1))
            }}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm"
          >
            {t('reservations.thisMonth')}
          </button>
          <button
            onClick={() => shiftMonth(1)}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            title={t('reservations.nextMonth')}
          >
            <Icon name="chevron-right" className="w-4" />
          </button>
        </div>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 border-b border-white/10">
        {dayNames.map((name) => (
          <div key={name} className="px-2 py-2 text-xs font-medium text-gray-500 text-center capitalize">
            {name}
          </div>
        ))}
      </div>

      {/* Month grid */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-400">{t('reservations.loading')}</div>
      ) : (
        <div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 divide-x divide-white/5 border-b border-white/5 last:border-b-0">
              {week.map((day) => {
                const key = isoDate(day)
                const dayReservations = byDate.get(key) || []
                const isToday = key === today
                const inMonth = day.getMonth() === monthStart.getMonth()
                const extra = dayReservations.length - MAX_CHIPS_PER_DAY
                return (
                  <div
                    key={key}
                    onClick={() => onJumpToWeek(key)}
                    className={`min-h-[88px] p-1.5 cursor-pointer transition-colors hover:bg-white/5 ${inMonth ? '' : 'bg-black/20'}`}
                  >
                    <div className={`px-1 text-xs font-medium flex items-center gap-1.5 ${isToday ? 'text-[#006cd4]' : inMonth ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span>{day.getDate()}</span>
                      {isToday && <span className="w-1.5 h-1.5 rounded-full bg-[#006cd4]" />}
                    </div>
                    <div className="mt-1 space-y-1">
                      {dayReservations.slice(0, MAX_CHIPS_PER_DAY).map((r) => (
                        <button
                          key={r.id}
                          onClick={(e) => { e.stopPropagation(); setSelected(r) }}
                          className={`w-full text-left px-1.5 py-0.5 rounded border text-[11px] leading-tight truncate transition-colors hover:brightness-125 ${statusChipColor(r.status)}`}
                        >
                          {normalizeTime(r.reservation_time)} {r.customer_name}
                        </button>
                      ))}
                      {extra > 0 && (
                        <p className="px-1 text-[11px] text-gray-500">+{extra}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ReservationDetailModal reservation={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}
